import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { SignalData, TileTraffic } from '../../gen/traffic/network';
import { PedNet, PedSim } from '../../sim/peds';
import { type Lane, type Obstacle, TrafficNet, TrafficSim } from '../../sim/traffic';
import type { TileManager } from '../tileManager';
import { carBodyGeometry } from '../vehicleModels';
import type { ModelLibrary } from '../models/library';
import { carPaintMaterial, personDepthMaterial, personMaterial } from '../models/materials';

/** Cars and pedestrians kept around the camera, per quality preset. */
export const CROWD = { low: { cars: 25, peds: 40 }, mid: { cars: 70, peds: 140 }, high: { cars: 110, peds: 220 } };

const STEP = 0.05;
const CAR_RANGE = { spawnMin: 50, spawnMax: 230, drop: 300 };
const PED_RANGE = { spawnMin: 15, spawnMax: 140, drop: 170 };
const PAINT: [number, number, number][] = [
  [0.6, 0.08, 0.07],
  [0.08, 0.15, 0.4],
  [0.75, 0.74, 0.7],
  [0.1, 0.1, 0.11],
  [0.25, 0.35, 0.22],
  [0.72, 0.6, 0.18],
  [0.45, 0.45, 0.47],
  [0.9, 0.88, 0.84],
];
const SHIRTS: [number, number, number][] = [
  [0.7, 0.12, 0.1],
  [0.12, 0.25, 0.55],
  [0.85, 0.85, 0.82],
  [0.15, 0.15, 0.16],
  [0.3, 0.45, 0.25],
  [0.8, 0.6, 0.2],
  [0.45, 0.3, 0.5],
  [0.55, 0.55, 0.58],
];

/**
 * Traffic and pedestrians around the camera (DECISIONS "Traffic"). Follows the near tiles' networks
 * (TileState.traffic), keeps a quota of cars and walkers alive between spawn and drop distances,
 * steps the simulations (src/sim) at 20 Hz and draws them as instances, interpolated between steps:
 * cars with the parked-car models, pedestrians with a vertex-shader walk cycle, and the lit lamp of
 * every traffic light near the camera.
 */
export class TrafficSystem {
  readonly net = new TrafficNet();
  readonly sim: TrafficSim;
  readonly pedNet = new PedNet();
  readonly peds: PedSim;
  readonly root = new THREE.Group();
  private readonly tileData = new Map<string, TileTraffic>();
  private signals: SignalData[] = [];
  private laneList: Lane[] = [];
  private cars: THREE.InstancedMesh[] = [];
  /** Model pedestrians (one instanced mesh per character) and their per-instance animation data. */
  private people: { mesh: THREE.InstancedMesh; anim: THREE.InstancedBufferAttribute; walkSeconds: number }[] = [];
  private time = 0;
  private readonly pedMesh: THREE.InstancedMesh;
  private readonly pedData: THREE.InstancedBufferAttribute;
  private readonly lamps: THREE.InstancedMesh;
  /** Tail lights (two per car), bright red when braking or stopped. */
  private readonly tails: THREE.InstancedMesh;
  /** Per body type: rear x, light height, half the lights' spacing (from the car's bounds). */
  private tailSpots: { x: number; y: number; w: number }[] = [];
  private acc = 0;
  private syncT = 0;
  private rand = (() => {
    let s = 99991;
    return () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
  })();
  counts = CROWD.mid;
  enabled = true;
  /** Where the last draw was between the last two simulation steps (0 … 1). */
  alpha = 0;

  constructor(
    private readonly tiles: TileManager,
    private readonly materials: (m: THREE.Material) => void,
    models?: ModelLibrary,
  ) {
    this.root.name = 'traffic';
    this.sim = new TrafficSim(this.net, 12345);
    this.peds = new PedSim(this.pedNet, 777);
    const paint = carPaintMaterial();
    materials(paint);
    for (let b = 0; b < 3; b++) {
      const m = new THREE.InstancedMesh(carBodyGeometry(b), paint, CROWD.high.cars);
      m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CROWD.high.cars * 3), 3);
      m.count = 0;
      m.frustumCulled = false;
      m.castShadow = true;
      m.receiveShadow = true;
      this.cars.push(m);
      this.root.add(m);
    }
    const pedGeo = pedGeometry();
    this.pedData = new THREE.InstancedBufferAttribute(new Float32Array(CROWD.high.peds * 4), 4);
    pedGeo.setAttribute('pedData', this.pedData);
    const pedMat = pedMaterial();
    materials(pedMat);
    this.pedMesh = new THREE.InstancedMesh(pedGeo, pedMat, CROWD.high.peds);
    this.pedMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CROWD.high.peds * 3), 3);
    this.pedMesh.count = 0;
    this.pedMesh.frustumCulled = false;
    this.pedMesh.castShadow = true;
    this.pedMesh.userData.noAO = true;
    this.root.add(this.pedMesh);
    models?.onLoad(() => this.useModels(models));
    this.lamps = new THREE.InstancedMesh(new THREE.BoxGeometry(0.2, 0.2, 0.04), new THREE.MeshBasicMaterial({ toneMapped: false }), 1024);
    this.lamps.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(1024 * 3), 3);
    this.lamps.count = 0;
    this.lamps.frustumCulled = false;
    this.root.add(this.lamps);
    // Two small lamps at z = ±1, scaled per car to its width.
    const tailGeo = mergeGeometries([new THREE.BoxGeometry(0.16, 0.07, 0.36).translate(0, 0, 1), new THREE.BoxGeometry(0.16, 0.07, 0.36).translate(0, 0, -1)])!;
    this.tails = new THREE.InstancedMesh(tailGeo, new THREE.MeshBasicMaterial({ toneMapped: false }), CROWD.high.cars);
    this.tails.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CROWD.high.cars * 3), 3);
    this.tails.count = 0;
    this.tails.frustumCulled = false;
    this.root.add(this.tails);
    this.measureTails();
  }

  /** Where each body type's tail lights sit, from its geometry's bounds (models or built-in shapes). */
  private measureTails(): void {
    this.tailSpots = this.cars.map((m) => {
      const g = m.geometry;
      if (!g.boundingBox) g.computeBoundingBox();
      const b = g.boundingBox!;
      // Set into the tapered tail so only the lens faces show.
      return { x: b.min.x + 0.1, y: b.min.y + (b.max.y - b.min.y) * 0.5, w: ((b.max.z - b.min.z) / 2) * 0.6 };
    });
  }

  /** Swap the built-in cars and people for the loaded models. */
  private useModels(lib: ModelLibrary): void {
    if (lib.cars.length) {
      const paint = this.cars[0]?.material as THREE.Material;
      for (const m of this.cars) m.removeFromParent();
      this.cars = lib.cars.map((c) => {
        const m = new THREE.InstancedMesh(c.geometry, paint, CROWD.high.cars);
        m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CROWD.high.cars * 3), 3);
        m.count = 0;
        m.frustumCulled = false;
        m.castShadow = true;
        m.receiveShadow = true;
        this.root.add(m);
        return m;
      });
      this.measureTails();
    }
    for (const p of lib.people) {
      const geo = p.geometry.clone();
      const anim = new THREE.InstancedBufferAttribute(new Float32Array(CROWD.high.peds * 4), 4);
      geo.setAttribute('pedAnim', anim);
      const mat = personMaterial(p);
      this.materials(mat);
      const mesh = new THREE.InstancedMesh(geo, mat, CROWD.high.peds);
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CROWD.high.peds * 3), 3);
      mesh.customDepthMaterial = personDepthMaterial(p);
      mesh.userData.noAO = true;
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.root.add(mesh);
      this.people.push({ mesh, anim, walkSeconds: p.walk.seconds });
    }
    if (this.people.length) this.pedMesh.visible = false;
  }

  /** Follow the loaded near tiles' networks (twice a second). */
  private sync(): void {
    const live = new Map<string, TileTraffic>();
    for (const t of this.tiles.tiles.values()) if (t.traffic && t.cityLevel === 2) live.set(t.key, t.traffic);
    let changed = false;
    for (const [k, d] of this.tileData) {
      if (live.get(k) === d) continue;
      this.net.removeTile(k);
      this.pedNet.removeTile(k);
      this.tileData.delete(k);
      changed = true;
    }
    for (const [k, d] of live) {
      if (this.tileData.has(k)) continue;
      this.net.addTile(k, d);
      this.pedNet.addTile(k, d);
      this.tileData.set(k, d);
      changed = true;
    }
    if (changed) {
      this.laneList = [...this.net.lanes.values()];
      this.signals = [...this.tileData.values()].flatMap((d) => d.signals);
      for (const p of this.peds.peds) if (!this.pedNet.hasTile(p.path.tile)) p.dead = true;
    }
  }

  private populate(cam: THREE.Vector3): void {
    const far2 = (x: number, z: number, r: number) => (x - cam.x) ** 2 + (z - cam.z) ** 2 > r * r;
    for (const c of this.sim.cars) if (far2(c.cur[0], c.cur[2], CAR_RANGE.drop)) this.sim.despawn(c);
    for (const p of this.peds.peds) if (far2(p.cur[0], p.cur[2], PED_RANGE.drop)) p.dead = true;
    for (let tries = 0; tries < 60 && this.sim.cars.length < this.counts.cars && this.laneList.length; tries++) {
      const lane = this.laneList[Math.floor(this.rand() * this.laneList.length)];
      const s = this.rand() * lane.length;
      const i = Math.min(lane.pts.length / 3 - 1, Math.floor((s / lane.length) * (lane.pts.length / 3)));
      const d = Math.hypot(lane.pts[i * 3] - cam.x, lane.pts[i * 3 + 2] - cam.z);
      if (d < CAR_RANGE.spawnMin || d > CAR_RANGE.spawnMax) continue;
      this.sim.spawn(lane, s, Math.floor(this.rand() * 12), PAINT[Math.floor(this.rand() * PAINT.length)]);
    }
    const paths = this.pedNet.paths;
    for (let tries = 0; tries < 60 && this.peds.peds.length < this.counts.peds && paths.length; tries++) {
      const path = paths[Math.floor(this.rand() * paths.length)];
      const s = this.rand() * path.length;
      const i = Math.min(path.pts.length / 3 - 1, Math.floor((s / path.length) * (path.pts.length / 3)));
      const d = Math.hypot(path.pts[i * 3] - cam.x, path.pts[i * 3 + 2] - cam.z);
      if (d < PED_RANGE.spawnMin || d > PED_RANGE.spawnMax) continue;
      this.peds.spawn(path, s);
    }
  }

  update(dt: number, camera: THREE.Camera, obstacles: Obstacle[]): void {
    this.root.visible = this.enabled;
    if (!this.enabled) return;
    this.syncT -= dt;
    if (this.syncT <= 0) {
      this.syncT = 0.5;
      this.sync();
      this.populate(camera.position);
    }
    this.acc = Math.min(this.acc + dt, STEP * 5);
    while (this.acc >= STEP) {
      this.sim.step(STEP, obstacles);
      this.peds.step(STEP, obstacles);
      this.acc -= STEP;
    }
    this.time += dt;
    this.alpha = this.acc / STEP;
    this.draw(this.alpha, camera.position);
  }

  private draw(alpha: number, cam: THREE.Vector3): void {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const p = new THREE.Vector3();
    const one = new THREE.Vector3(1, 1, 1);
    const up = new THREE.Vector3(0, 1, 0);
    const lerpAngle = (a: number, b: number) => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * alpha;
    // Cars follow the road's slope: nose-up pitch about the car's own z (after its heading).
    const qp = new THREE.Quaternion();
    const zAxis = new THREE.Vector3(0, 0, 1);
    const carPose = (c: (typeof this.sim.cars)[number]) => {
      p.set(c.prev[0] + (c.cur[0] - c.prev[0]) * alpha, c.prev[1] + (c.cur[1] - c.prev[1]) * alpha + c.lift, c.prev[2] + (c.cur[2] - c.prev[2]) * alpha);
      q.setFromAxisAngle(up, lerpAngle(c.prev[3], c.cur[3])).multiply(qp.setFromAxisAngle(zAxis, c.pitch));
    };
    const n = this.cars.map(() => 0);
    for (const c of this.sim.cars) {
      const b = c.body % this.cars.length;
      const mesh = this.cars[b];
      const i = n[b]++;
      if (i >= mesh.instanceMatrix.count) continue;
      carPose(c);
      m.compose(p, q, one);
      mesh.setMatrixAt(i, m);
      mesh.instanceColor!.setXYZ(i, c.color[0], c.color[1], c.color[2]);
    }
    // Tail lights: dim red, bright when braking or standing.
    let tl = 0;
    const off = new THREE.Matrix4();
    for (const c of this.sim.cars) {
      if (tl >= this.tails.instanceMatrix.count) break;
      if ((c.cur[0] - cam.x) ** 2 + (c.cur[2] - cam.z) ** 2 > 160 * 160) continue;
      const spot = this.tailSpots[c.body % this.cars.length];
      if (!spot) continue;
      carPose(c);
      m.compose(p, q, one).multiply(off.makeTranslation(spot.x, spot.y, 0).scale(new THREE.Vector3(1, 1, spot.w)));
      this.tails.setMatrixAt(tl, m);
      const braking = c.a < -0.6 || c.v < 0.3;
      if (braking) this.tails.instanceColor!.setXYZ(tl, 40, 1.2, 0.8);
      else this.tails.instanceColor!.setXYZ(tl, 2.5, 0.06, 0.04);
      tl++;
    }
    this.tails.count = tl;
    this.tails.instanceMatrix.needsUpdate = true;
    this.tails.instanceColor!.needsUpdate = true;
    this.cars.forEach((mesh, b) => {
      mesh.count = Math.min(n[b], mesh.instanceMatrix.count);
      mesh.instanceMatrix.needsUpdate = true;
      mesh.instanceColor!.needsUpdate = true;
    });
    // Model pedestrians: each walker on one of the characters, the walk clip in step with its stride.
    if (this.people.length) {
      const counts = this.people.map(() => 0);
      for (const w of this.peds.peds) {
        const mi = Math.floor(w.look * this.people.length * 7.1) % this.people.length;
        const pm = this.people[mi];
        const i = counts[mi]++;
        if (i >= pm.mesh.instanceMatrix.count) continue;
        p.set(w.prev[0] + (w.cur[0] - w.prev[0]) * alpha, w.prev[1] + (w.cur[1] - w.prev[1]) * alpha, w.prev[2] + (w.cur[2] - w.prev[2]) * alpha);
        q.setFromAxisAngle(up, lerpAngle(w.prev[3], w.cur[3]));
        m.compose(p, q, one);
        pm.mesh.setMatrixAt(i, m);
        const shirt = SHIRTS[Math.floor(w.look * SHIRTS.length)];
        pm.mesh.instanceColor!.setXYZ(i, shirt[0], shirt[1], shirt[2]);
        const idle = w.pause > 0;
        const hairSkin = Math.floor(((w.look * 13.7) % 1) * 4) * 4 + Math.floor(((w.look * 29.3) % 1) * 4);
        pm.anim.setXYZW(i, idle ? this.time + w.look * 10 : (w.phase / (Math.PI * 2)) * pm.walkSeconds, idle ? 1 : 0, Math.floor(((w.look * 7.3) % 1) * 4), hairSkin);
      }
      this.people.forEach((pm, i) => {
        pm.mesh.count = Math.min(counts[i], pm.mesh.instanceMatrix.count);
        pm.mesh.instanceMatrix.needsUpdate = true;
        pm.mesh.instanceColor!.needsUpdate = true;
        pm.anim.needsUpdate = true;
      });
    }
    let k = 0;
    for (const w of this.people.length ? [] : this.peds.peds) {
      if (k >= this.pedMesh.instanceMatrix.count) break;
      p.set(w.prev[0] + (w.cur[0] - w.prev[0]) * alpha, w.prev[1] + (w.cur[1] - w.prev[1]) * alpha, w.prev[2] + (w.cur[2] - w.prev[2]) * alpha);
      q.setFromAxisAngle(up, lerpAngle(w.prev[3], w.cur[3]));
      m.compose(p, q, one);
      this.pedMesh.setMatrixAt(k, m);
      const shirt = SHIRTS[Math.floor(w.look * SHIRTS.length)];
      this.pedMesh.instanceColor!.setXYZ(k, shirt[0], shirt[1], shirt[2]);
      this.pedData.setXYZW(k, w.phase, (w.look * 7.3) % 1, (w.look * 13.7) % 1, w.pause > 0 ? 0 : 1);
      k++;
    }
    this.pedMesh.count = k;
    this.pedMesh.instanceMatrix.needsUpdate = true;
    this.pedMesh.instanceColor!.needsUpdate = true;
    this.pedData.needsUpdate = true;
    // Lit lamps of the traffic lights within 250 m.
    let l = 0;
    for (const s of this.signals) {
      if (l >= this.lamps.instanceMatrix.count) break;
      if ((s.x - cam.x) ** 2 + (s.z - cam.z) ** 2 > 250 * 250) continue;
      const st = this.net.light(s.node, s.dir);
      const [fx, fz] = s.facing;
      // As drawn by addStreetProps: head 3.2 m along the arm (to the facing's left), lamps 18 cm in front.
      const lx = fz;
      const lz = -fx;
      const h = st === 'red' ? 5.25 : st === 'yellow' ? 4.95 : 4.65;
      p.set(s.x + lx * 3.2 + fx * 0.19, s.y + h, s.z + lz * 3.2 + fz * 0.19);
      q.setFromAxisAngle(up, Math.atan2(-fz, fx) + Math.PI / 2);
      m.compose(p, q, one);
      this.lamps.setMatrixAt(l, m);
      // Bright enough to bloom (the threshold follows exposure: about 9 outdoors).
      const c = st === 'red' ? [40, 1.4, 1] : st === 'yellow' ? [38, 24, 1.8] : [1.6, 34, 11];
      this.lamps.instanceColor!.setXYZ(l, c[0], c[1], c[2]);
      l++;
    }
    this.lamps.count = l;
    this.lamps.instanceMatrix.needsUpdate = true;
    this.lamps.instanceColor!.needsUpdate = true;
  }
}

/**
 * A low-poly person, 1.75 m, facing +x: `limb` (0 body, 1/2 legs, 3/4 arms) drives the walk cycle,
 * `part` (0 skin, 1 shirt, 2 trousers, 3 shoes) the colours.
 */
function pedGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const box = (sx: number, sy: number, sz: number, x: number, y: number, z: number, limb: number, part: number) => {
    const g = new THREE.BoxGeometry(sx, sy, sz).translate(x, y, z).toNonIndexed();
    g.deleteAttribute('uv');
    const n = g.getAttribute('position').count;
    g.setAttribute('limb', new THREE.BufferAttribute(new Float32Array(n).fill(limb), 1));
    g.setAttribute('part', new THREE.BufferAttribute(new Float32Array(n).fill(part), 1));
    parts.push(g);
  };
  for (const [side, limb] of [
    [-1, 1],
    [1, 2],
  ]) {
    box(0.15, 0.82, 0.15, 0, 0.49, side * 0.1, limb, 2); // leg
    box(0.26, 0.08, 0.13, 0.04, 0.04, side * 0.1, limb, 3); // shoe
  }
  box(0.24, 0.58, 0.4, 0, 1.19, 0, 0, 1); // torso
  box(0.2, 0.23, 0.19, 0.01, 1.62, 0, 0, 0); // head
  for (const [side, limb] of [
    [-1, 3],
    [1, 4],
  ]) {
    box(0.1, 0.58, 0.1, 0, 1.17, side * 0.26, limb, 1); // arm
    box(0.09, 0.09, 0.09, 0, 0.84, side * 0.26, limb, 0); // hand
  }
  const g = mergeGeometries(parts)!;
  for (const p of parts) p.dispose();
  return g;
}

/** Walk cycle and colours in the vertex shader (per-instance pedData: phase, trousers, skin, moving). */
function pedMaterial(): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ roughness: 0.85 });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float limb;\nattribute float part;\nattribute vec4 pedData;\nvarying vec3 vPedColor;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        if (limb > 0.5) {
          bool arm = limb > 2.5;
          float swing = sin(pedData.x + ((limb > 1.5 && limb < 2.5) || limb > 3.5 ? 3.14159 : 0.0)) * (arm ? -0.45 : 0.55) * pedData.w;
          vec3 pivot = vec3(0.0, arm ? 1.45 : 0.9, 0.0);
          vec3 d = transformed - pivot;
          float c = cos(swing);
          float s = sin(swing);
          transformed = pivot + vec3(d.x * c - d.y * s, d.x * s + d.y * c, d.z);
        }
        // Colours: skin, shirt (instanceColor), trousers, shoes.
        vec3 skins[4] = vec3[4](vec3(0.87, 0.68, 0.55), vec3(0.62, 0.43, 0.3), vec3(0.4, 0.27, 0.18), vec3(0.95, 0.8, 0.68));
        vec3 pants[4] = vec3[4](vec3(0.12, 0.14, 0.22), vec3(0.08, 0.08, 0.09), vec3(0.45, 0.4, 0.32), vec3(0.3, 0.32, 0.36));
        #ifdef USE_INSTANCING_COLOR
          vec3 shirt = instanceColor;
        #else
          vec3 shirt = vec3(0.5);
        #endif
        vPedColor = part < 0.5 ? skins[int(pedData.z * 3.99)] : part < 1.5 ? shirt : part < 2.5 ? pants[int(pedData.y * 3.99)] : vec3(0.06);`,
      );
    sh.fragmentShader = sh.fragmentShader.replace('#include <common>', '#include <common>\nvarying vec3 vPedColor;').replace('#include <color_fragment>', 'diffuseColor.rgb = vPedColor;');
  };
  m.customProgramCacheKey = () => 'pedestrian';
  return m;
}
